import React, { useEffect } from "react";
import { wipeData } from "@braze/web-sdk";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Outlet,
} from "react-router-dom";
import { Page404 } from "component-library";
import { useTranslation } from "i18n";
import { FEATURE_FLAGS, initBraze } from "shared-services";
import { BillingUser } from "translations/src/models/billing";
import { CD_TYPE_CODE } from "./constants/PerformerType";
import { config, JOINING_APP_ROUTES } from "./config/common";
import "./utils/ensure-basename";
import { getGclid } from "./utils";
import DetailsPageGrad from "./pages/JoiningPages/Graduate/DetailsPageGrad";
import FeatureFlagProtectedRoute from "./components/FeatureFlagProtectedRoute";

/**
 * Used for code splitting. Will only load page when required
 */
const JoiningPageMain = React.lazy(() =>
  import("./pages/JoiningPages/Performers/MainPerformer/JoiningPageMain").then(
    (module) => ({
      default: module.default,
    })
  )
);
const WelcomePageYP = React.lazy(() =>
  import("./pages/JoiningPages/Performers/YoungPerformer/WelcomePageYP").then(
    (module) => ({
      default: module.default,
    })
  )
);
const WelcomePageCD = React.lazy(() =>
  import("./pages/JoiningPages/CastingDirector/WelcomePageCD").then(
    (module) => ({
      default: module.default,
    })
  )
);
const CheckoutCompletePage = React.lazy(() =>
  import("./pages/JoiningPages/Common/CheckoutCompletePage").then((module) => ({
    default: module.default,
  }))
);
const JoiningPageYPMain = React.lazy(() =>
  import("./pages/JoiningPages/Performers/YoungPerformer").then((module) => ({
    default: module.default,
  }))
);
const JoiningPageCastingDirectorMain = React.lazy(() =>
  import("./pages/JoiningPages/CastingDirector").then((module) => ({
    default: module.default,
  }))
);
const PreviouslyComplete = React.lazy(() =>
  import(
    "./pages/JoiningPages/Performers/MainPerformer/PreviouslyComplete/PreviouslyComplete"
  ).then((module) => ({
    default: module.default,
  }))
);
const CompletePageCD = React.lazy(() =>
  import("./pages/JoiningPages/CastingDirector/CompletePageCD").then(
    (module) => ({
      default: module.default,
    })
  )
);
const LoginPage = React.lazy(() =>
  import("./pages/LoginPage/LoginPage").then((module) => ({
    default: module.LoginPage,
  }))
);
const ResumePage = React.lazy(() =>
  import("./pages/Resume/ResumePage").then((module) => ({
    default: module.default,
  }))
);
const GraduateStartApplication = React.lazy(() =>
  import("./pages/JoiningPages/Graduate/StartApplicationPage").then(
    (module) => ({
      default: module.default,
    })
  )
);
const GraduateAboutYouPage = React.lazy(() =>
  import("./pages/JoiningPages/Graduate/AboutYouPageGrad").then((module) => ({
    default: module.default,
  }))
);
const GraduateReviewPage = React.lazy(() =>
  import("./pages/JoiningPages/Graduate/ReviewPage").then((module) => ({
    default: module.default,
  }))
);
const GraduateCheckoutJoiningPage = React.lazy(() =>
  import("./pages/JoiningPages/Graduate/CheckoutJoining").then((module) => ({
    default: module.default,
  }))
);
const ContinueApplicationGrads = React.lazy(() =>
  import("./pages/JoiningPages/Graduate/ContinueApplicationGrads").then(
    (module) => ({
      default: module.default,
    })
  )
);
const GraduateCheckout = React.lazy(() =>
  import("./pages/JoiningPages/Graduate/Checkout").then((module) => ({
    default: module.default,
  }))
);
const GraduateComplete = React.lazy(() =>
  import("./pages/JoiningPages/Graduate/Complete").then((module) => ({
    default: module.default,
  }))
);

const JoiningPageAgent = React.lazy(() =>
  import("./pages/JoiningPages/Agent/JoiningPageAgent").then((module) => ({
    default: module.default,
  }))
);

const JoiningPageAgentComplete = React.lazy(() =>
  import("./pages/JoiningPages/Agent/AgentComplete").then((module) => ({
    default: module.default,
  }))
);

const GraduateRoutesProtectedByFeatureFlag = () => {
  return (
    <FeatureFlagProtectedRoute flagName={FEATURE_FLAGS.Grads2024Launch}>
      <Outlet />
    </FeatureFlagProtectedRoute>
  );
};

const AppRoutes = () => {
  const { t } = useTranslation("joining");

  getGclid();

  useEffect(() => {
    try {
      // required here to init braze and drop all data if user was logged in previously
      initBraze({
        memberGroupFromToken: "any",
        portalId: "",
        brazeConfig: {
          apiKey: config.braze.apiKey,
          baseUrl: config.braze.baseUrl,
        },
      });

      wipeData();
    } catch (error) {
      console.error(error);
    }
  }, []);

  return (
    <Router basename={process.env.PUBLIC_URL}>
      <Routes>
        <Route
          path={JOINING_APP_ROUTES.PERFORMER.MAIN}
          element={<JoiningPageMain />}
        />
        <Route
          path={JOINING_APP_ROUTES.YOUNG_PERFORMER.ROOT_WELCOME}
          element={<WelcomePageYP />}
        />
        <Route
          path={JOINING_APP_ROUTES.YOUNG_PERFORMER.MAIN}
          element={<JoiningPageYPMain />}
        />
        <Route
          path={JOINING_APP_ROUTES.CASTING_DIRECTOR.ROOT_WELCOME}
          element={<WelcomePageCD />}
        />
        <Route
          path={JOINING_APP_ROUTES.CASTING_DIRECTOR.MAIN}
          element={<JoiningPageCastingDirectorMain />}
        />
        <Route
          path={JOINING_APP_ROUTES.CASTING_DIRECTOR.RESUME}
          element={<ResumePage userType={CD_TYPE_CODE} />}
        />
        <Route
          path={JOINING_APP_ROUTES.CASTING_DIRECTOR.MEMBER_COMPLETE}
          element={<CompletePageCD isMemberComplete />}
        />
        <Route
          path={JOINING_APP_ROUTES.CASTING_DIRECTOR.COMPLETE}
          element={<CompletePageCD />}
        />
        <Route
          path={JOINING_APP_ROUTES.PERFORMER.COMPLETE}
          element={<CheckoutCompletePage userType={BillingUser.Performer} />}
        />
        <Route
          path={JOINING_APP_ROUTES.YOUNG_PERFORMER.COMPLETE}
          element={
            <CheckoutCompletePage userType={BillingUser.YoungPerformer} />
          }
        />
        <Route
          path={JOINING_APP_ROUTES.PERFORMER.PREVIOUSLY_COMPLETE}
          element={<PreviouslyComplete />}
        />

        <Route element={<GraduateRoutesProtectedByFeatureFlag />}>
          <Route
            path={JOINING_APP_ROUTES.GRADUATE.START_APP}
            element={<GraduateStartApplication />}
          />
          <Route
            path={JOINING_APP_ROUTES.GRADUATE.ABOUT_YOU}
            element={<GraduateAboutYouPage />}
          />
          <Route
            path={JOINING_APP_ROUTES.GRADUATE.DETAILS}
            element={<DetailsPageGrad />}
          />
          <Route
            path={JOINING_APP_ROUTES.GRADUATE.REVIEW}
            element={<GraduateReviewPage />}
          />
          <Route
            path={JOINING_APP_ROUTES.GRADUATE.CONTINUE_APP}
            element={<ContinueApplicationGrads />}
          />
          <Route
            path={JOINING_APP_ROUTES.GRADUATE.SUBSCRIPTION}
            element={<GraduateCheckoutJoiningPage />}
          />
          <Route
            path={JOINING_APP_ROUTES.GRADUATE.CHECKOUT_JOINING}
            element={<GraduateCheckoutJoiningPage />}
          />
        </Route>

        <Route
          path={JOINING_APP_ROUTES.GRADUATE.CHECKOUT}
          element={<GraduateCheckout />}
        />
        <Route
          path={JOINING_APP_ROUTES.GRADUATE.COMPLETE}
          element={<GraduateComplete />}
        />
        <Route
          path={JOINING_APP_ROUTES.AGENT.ROOT_WELCOME}
          element={<JoiningPageAgent />}
        />
        <Route
          path={JOINING_APP_ROUTES.AGENT.COMPLETE}
          element={<JoiningPageAgentComplete />}
        />
        <Route path={JOINING_APP_ROUTES.ROOT} element={<LoginPage />} />
        <Route path={JOINING_APP_ROUTES.RESUME} element={<ResumePage />} />
        <Route
          path="*"
          element={
            <Page404
              texts={{
                logoLabel: t("common:page404.logoLabel"),
                headerText: t("common:page404.headerText"),
                messageText: t("common:page404.messageText"),
                buttonText: t("common:page404.buttonText"),
              }}
              homePageLink={JOINING_APP_ROUTES.BASE}
            />
          }
        />
      </Routes>
    </Router>
  );
};

export default AppRoutes;
